/**
 * ステップ見積もりの項目表エディタ。
 *
 * ショット編集（ShotTable / MediaItemTable）の設計思想を踏襲した
 * スプレッドシート風のインライン編集テーブル:
 *  - セルはクリックしてそのまま編集（編集はローカル state に即時反映。
 *    永続化はプリセットストリップの保存操作で行う）
 *  - 行頭ハンドルで dnd-kit による縦並び替え
 *  - 行の追加・複製・削除
 *  - 詳細行（説明テキスト・サムネイル×2 と数量可変の範囲設定）は
 *    シェブロンで展開
 *  - サムネイルは purpose=tool_asset で S3 へアップロードし URL を保持
 *
 * 数量可変の範囲（下限・上限・初期値）は詳細行に置く。表本体のセルは
 * トグルと現在の範囲サマリーだけにして、ON/OFF で行の高さが変わらないようにする。
 *
 * `_a` `_b` サフィックスによるグループ化の結果は、表の上の
 * 「ステップ構成プレビュー」チップで常に確認できる。
 */
import React, { useMemo, useRef, useState } from "react";
import styled from "styled-components";
import toast from "react-hot-toast";

import IconButton from "../../../../components/button/IconButton";
import DropdownMenu from "../../../../components/dropdown-menu/DropdownMenu";
import Button from "../../../../components/button/Button";
import Toggle from "../../../../components/form-item/Toggle";
import SortableList from "../../../../components/sortable/SortableList";
import SortableItem from "../../../../components/sortable/SortableItem";
import SortableDragHandle from "../../../../components/sortable/SortableDragHandle";
import AiImageGeneratorModal from "../../../../components/ai-image-generator/AiImageGeneratorModal";
import type { GeneratedImageCandidate } from "../../../../app/services/aiImage";
import { useInitiateUploadMutation } from "../../../../app/services/uploadApi";
import {
  buildSteps,
  calcLineAmount,
  formatPrice,
  isPercentUnit,
  isWizardStep,
  parseNumericPrice,
  parseRowName,
  resolveUnitPrice,
  type EstimateItemRow,
} from "../utils/estimateModel";
import { createEmptyRow } from "../utils/presetPayload";
import { uploadToolAsset } from "../utils/thumbnailUpload";

// ===== スタイル =====

const TableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
  border: 1px solid var(--soremo-border);
  border-radius: 8px;
  background: #fff;
`;

const StyledTable = styled.table`
  width: 100%;
  min-width: 920px;
  border-collapse: collapse;

  th {
    padding: 8px;
    text-align: left;
    white-space: nowrap;
    background: var(--soremo-background);
    border-bottom: 1px solid var(--soremo-border);
  }

  td {
    padding: 4px 6px;
    border-bottom: 1px solid var(--soremo-border);
    vertical-align: top;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

const CellInput = styled.input.attrs({ className: "bodytext-13" })`
  width: 100%;
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 6px 8px;
  background: transparent;

  &:hover {
    border-color: var(--soremo-border);
  }

  &:focus {
    outline: none;
    border-color: var(--soremo-blue);
    background: #fff;
  }
`;

const NumberCellInput = styled(CellInput)`
  text-align: right;
`;

const MiniNumberInput = styled.input.attrs({ className: "bodytext-11" })`
  width: 44px;
  border: 1px solid var(--soremo-border);
  border-radius: 4px;
  padding: 3px 4px;
  text-align: right;

  &:focus {
    outline: none;
    border-color: var(--soremo-blue);
  }
`;

const CellHint = styled.span.attrs({ className: "bodytext-11" })`
  display: block;
  padding: 0 8px;
  color: var(--soremo-gray);
  white-space: nowrap;
`;

/**
 * 数量可変の範囲サマリー。クリックで詳細行を開いて編集する。
 * セル内に入力欄を並べると行の高さが変わってしまうため、表示は 1 行に固定する。
 */
const RangeSummaryButton = styled.button.attrs({ className: "bodytext-11" })`
  border: 1px solid var(--soremo-border);
  border-radius: 10px;
  padding: 2px 10px;
  background: #fff;
  color: var(--soremo-gray);
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    border-color: var(--soremo-blue);
    color: var(--soremo-blue);
  }
`;

const DetailCell = styled.td`
  background: var(--soremo-background);
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 200px;
  gap: 12px 16px;
  padding: 12px 16px 16px;
`;

/** 詳細行の中で横幅いっぱいに広げるブロック（数量可変の範囲設定など）。 */
const DetailFullRow = styled.div.attrs({ className: "u-col u-gap4" })`
  grid-column: 1 / -1;
`;

const DetailTextarea = styled.textarea.attrs({ className: "bodytext-13" })`
  width: 100%;
  min-height: 64px;
  border: 1px solid var(--soremo-border);
  border-radius: 4px;
  padding: 6px 8px;
  background: #fff;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--soremo-blue);
  }
`;

const ThumbBox = styled.div<{ $square?: boolean }>`
  position: relative;
  /* オプションサムネはウィザードの選択肢カードで正方形表示されるため、
     エディタのプレビューも正方形にしてトリミングを一致させる */
  width: ${({ $square }) => ($square ? "96px" : "100%")};
  height: 96px;
  border: 1px dashed var(--soremo-border);
  border-radius: 6px;
  background: #fff;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ThumbUploadLabel = styled.label.attrs({ className: "bodytext-11" })`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  height: 100%;
  color: var(--soremo-gray);
  cursor: pointer;

  .material-symbols-rounded {
    font-size: 22px;
    color: var(--soremo-light-gray);
  }

  &:hover {
    color: var(--soremo-blue);

    .material-symbols-rounded {
      color: var(--soremo-blue);
    }
  }

  input {
    display: none;
  }
`;

const ThumbRemoveButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  cursor: pointer;

  .material-symbols-rounded {
    font-size: 14px;
  }
`;

const StepChip = styled.span.attrs({ className: "bodytext-11" })<{
  $kind: "choice" | "toggle" | "fixed";
}>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  border-radius: 10px;
  border: 1px solid
    ${({ $kind }) =>
      $kind === "fixed" ? "var(--soremo-border)" : "var(--soremo-blue)"};
  color: ${({ $kind }) =>
    $kind === "fixed" ? "var(--soremo-gray)" : "var(--soremo-blue)"};
  background: #fff;
  white-space: nowrap;
`;

/**
 * `_a`/`_b` 選択肢グループの行ブロックを視覚的に束ねる左アクセント
 * （ショット編集 MediaItemTable の比較グループと同じ縦線）。
 */
const CHOICE_GROUP_ACCENT_STYLE: React.CSSProperties = {
  borderLeft: "3px solid var(--soremo-blue)",
};

// ===== 補助 =====

type ThumbField = "itemThumbnailUrl" | "optionThumbnailUrl";

const parseIntOr = (raw: string, fallback: number): number => {
  const n = parseInt(raw.replace(/[^\d-]/g, ""), 10);
  return Number.isFinite(n) ? n : fallback;
};

/** 複製時、グループ行なら空いている次のサフィックスを探す（a〜z）。 */
const duplicatedName = (source: EstimateItemRow, rows: EstimateItemRow[]): string => {
  const { base, suffix } = parseRowName(source.name);
  if (suffix === null) return source.name;
  const used = new Set(
    rows
      .map((r) => parseRowName(r.name))
      .filter((p) => p.base === base && p.suffix !== null)
      .map((p) => (p.suffix as string).toLowerCase()),
  );
  for (let i = 0; i < 26; i += 1) {
    const candidate = String.fromCharCode(97 + i); // a..z
    if (!used.has(candidate)) return `${base}_${candidate}`;
  }
  return source.name;
};

/** AI 生成に渡す対象項目のラベル。オプションサムネイルなら備考（選択肢名）を添える。 */
const aiItemLabelOf = (row: EstimateItemRow, isOption: boolean): string => {
  const { base } = parseRowName(row.name);
  const name = base || row.name;
  const note = row.note.trim();
  return isOption && note ? `${name}（${note}）` : name;
};

/** AI 生成に渡す補足説明。生成対象のサムネイルに近い方のテキストを優先する。 */
const aiItemDescOf = (row: EstimateItemRow, isOption: boolean): string =>
  isOption
    ? row.optionText.trim() || row.itemDescription.trim()
    : row.itemDescription.trim() || row.optionText.trim();

// ===== 本体 =====

export interface ItemTableEditorProps {
  rows: EstimateItemRow[];
  // eslint-disable-next-line no-unused-vars
  onChange: (rows: EstimateItemRow[]) => void;
}

const ItemTableEditor: React.FC<ItemTableEditorProps> = ({ rows, onChange }) => {
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(new Set());
  /** アップロード中のセル（`${rowId}:${field}`）。 */
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  /** AI 生成モーダルの対象セル。 */
  const [aiTarget, setAiTarget] = useState<{
    rowId: string;
    field: ThumbField;
  } | null>(null);
  const [initiateUpload] = useInitiateUploadMutation();
  const fileInputResetRef = useRef(0);

  const steps = useMemo(() => buildSteps(rows), [rows]);

  /**
   * ステップ構成プレビュー用。ウィザードに出るステップだけ通し番号を振り、
   * 選択肢が無く数量も固定の項目は「確認のみ」として番号を振らない。
   */
  const stepChips = useMemo(() => {
    let order = 0;
    return steps.map((step) => {
      const inWizard = isWizardStep(step);
      if (inWizard) order += 1;
      return { step, inWizard, order: inWizard ? order : null };
    });
  }, [steps]);

  // `_a`/`_b` 選択肢グループ（同一ベース名のサフィックス行が2行以上）のベース名。
  // 該当行には縦線アクセントを付けてグループを視覚的に束ねる
  const choiceGroupBases = useMemo(() => {
    const counts = new Map<string, number>();
    rows.forEach((row) => {
      const { base, suffix } = parseRowName(row.name);
      if (suffix !== null && base) counts.set(base, (counts.get(base) ?? 0) + 1);
    });
    return new Set(
      Array.from(counts.entries())
        .filter(([, count]) => count >= 2)
        .map(([base]) => base),
    );
  }, [rows]);

  const patchRow = (id: string, patch: Partial<EstimateItemRow>) => {
    onChange(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const addRow = () => {
    onChange([...rows, createEmptyRow()]);
  };

  const duplicateRow = (id: string) => {
    const index = rows.findIndex((row) => row.id === id);
    if (index < 0) return;
    const source = rows[index];
    const copy: EstimateItemRow = {
      ...source,
      id: crypto.randomUUID(),
      name: duplicatedName(source, rows),
    };
    const next = [...rows];
    next.splice(index + 1, 0, copy);
    onChange(next);
  };

  const removeRow = (id: string) => {
    onChange(rows.filter((row) => row.id !== id));
  };

  const reorderRows = (orderedIds: string[]) => {
    const byId = new Map(rows.map((row) => [row.id, row]));
    const next = orderedIds
      .map((id) => byId.get(id))
      .filter((row): row is EstimateItemRow => row !== undefined);
    if (next.length === rows.length) onChange(next);
  };

  const toggleExpanded = (id: string) => {
    setExpandedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandRow = (id: string) => {
    setExpandedRowIds((prev) => new Set(prev).add(id));
  };

  const handleThumbSelect = async (
    row: EstimateItemRow,
    field: ThumbField,
    file: File | undefined,
  ) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("サムネイルには画像ファイルを指定してください");
      return;
    }
    const key = `${row.id}:${field}`;
    setUploadingKey(key);
    try {
      const uploaded = await uploadToolAsset(initiateUpload, file);
      patchRow(row.id, { [field]: uploaded.url } as Partial<EstimateItemRow>);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "画像のアップロードに失敗しました");
    } finally {
      setUploadingKey((prev) => (prev === key ? null : prev));
      fileInputResetRef.current += 1;
    }
  };

  const renderThumbBox = (row: EstimateItemRow, field: ThumbField, label: string) => {
    const url = row[field];
    const key = `${row.id}:${field}`;
    const isUploading = uploadingKey === key;
    return (
      <div className="u-col u-gap4">
        <ThumbBox $square={field === "optionThumbnailUrl"}>
          {url ? (
            <>
              <img src={url} alt={label} />
              <ThumbRemoveButton
                type="button"
                aria-label={`${label}を削除`}
                onClick={() =>
                  patchRow(row.id, { [field]: "" } as Partial<EstimateItemRow>)
                }
              >
                <span className="material-symbols-rounded">close</span>
              </ThumbRemoveButton>
            </>
          ) : (
            <ThumbUploadLabel>
              <span className="material-symbols-rounded">
                {isUploading ? "hourglass_top" : "add_photo_alternate"}
              </span>
              {isUploading ? "アップロード中…" : `${label}を選択`}
              <input
                key={fileInputResetRef.current}
                type="file"
                accept="image/*"
                disabled={isUploading}
                onChange={(e) => handleThumbSelect(row, field, e.target.files?.[0])}
              />
            </ThumbUploadLabel>
          )}
        </ThumbBox>
        <Button
          size="small"
          theme="secondary"
          variant="outlined"
          width="fit"
          leftIcon="auto_awesome"
          disabled={isUploading}
          onClick={() => setAiTarget({ rowId: row.id, field })}
        >
          AIで生成
        </Button>
      </div>
    );
  };

  /** 数量可変の範囲設定（詳細行に置く。表本体の行高さを一定に保つため）。 */
  const renderQuantityRangeEditor = (row: EstimateItemRow) => (
    <DetailFullRow>
      <span className="bodytext-11" style={{ color: "var(--soremo-gray)" }}>
        数量可変の範囲（ウィザードでこの範囲から選べます）
      </span>
      <div
        className="u-row u-gap4 u-align-center bodytext-11"
        style={{ color: "var(--soremo-gray)" }}
      >
        <MiniNumberInput
          value={String(row.quantityMin)}
          aria-label="数量の下限"
          onChange={(e) =>
            patchRow(row.id, {
              quantityMin: parseIntOr(e.target.value, row.quantityMin),
            })
          }
        />
        〜
        <MiniNumberInput
          value={String(row.quantityMax)}
          aria-label="数量の上限"
          onChange={(e) =>
            patchRow(row.id, {
              quantityMax: parseIntOr(e.target.value, row.quantityMax),
            })
          }
        />
        初期
        <MiniNumberInput
          value={String(row.quantityDefault)}
          aria-label="数量の初期値"
          onChange={(e) =>
            patchRow(row.id, {
              quantityDefault: parseIntOr(e.target.value, row.quantityDefault),
            })
          }
        />
      </div>
    </DetailFullRow>
  );

  // ── AI サムネイル生成 ──────────────────────────────────────
  const aiRow = aiTarget ? (rows.find((r) => r.id === aiTarget.rowId) ?? null) : null;
  const aiIsOption = aiTarget?.field === "optionThumbnailUrl";

  /**
   * 生成候補（presigned URL・1 時間で失効）を取得し直して
   * purpose=tool_asset で恒久 URL へ再アップロードする
   * （プロジェクトロゴの AI 生成と同じ方式）。
   */
  const handleAiGenerated = async (candidate: GeneratedImageCandidate) => {
    if (!aiTarget) return;
    const res = await fetch(candidate.url);
    if (!res.ok) {
      throw new Error(`候補画像の取得に失敗しました（${res.status}）`);
    }
    const blob = await res.blob();
    const file = new File([blob], `estimate-thumb-${Date.now()}.png`, {
      type: blob.type || "image/png",
    });
    const uploaded = await uploadToolAsset(initiateUpload, file);
    patchRow(aiTarget.rowId, {
      [aiTarget.field]: uploaded.url,
    } as Partial<EstimateItemRow>);
    setAiTarget(null);
  };

  /**
   * 単価セルの補足表示（参照解決 / 百分率 / 計算外）。
   * 単位が % の行は「単価の何 % がいくらになるか」まで出す。
   */
  const priceHint = (row: EstimateItemRow): string | null => {
    const trimmed = row.unitPrice.trim();
    if (trimmed === "") return null;
    const isReference = parseNumericPrice(trimmed) === null;
    const resolved = resolveUnitPrice(row, rows);
    if (resolved.kind === "text") return "表示のみ（計算外）";

    if (isPercentUnit(row.unit)) {
      const quantity = row.quantityVariable ? row.quantityDefault : row.quantity;
      const amount = calcLineAmount(resolved.value, quantity, row.unit);
      const base = isReference
        ? `参照 → ${formatPrice(resolved.value)}`
        : formatPrice(resolved.value);
      return `${base} の ${quantity}% = ${formatPrice(amount)}`;
    }
    return isReference ? `参照 → ${formatPrice(resolved.value)}` : null;
  };

  const hasDetail = (row: EstimateItemRow): boolean =>
    Boolean(
      row.itemDescription.trim() ||
        row.itemThumbnailUrl.trim() ||
        row.optionText.trim() ||
        row.optionThumbnailUrl.trim() ||
        row.quantityVariable,
    );

  return (
    <div className="u-col u-gap12">
      {/* ステップ構成プレビュー */}
      <div className="u-row u-gap8 u-align-center" style={{ flexWrap: "wrap" }}>
        <span className="bodytext-11" style={{ color: "var(--soremo-gray)" }}>
          ステップ構成:
        </span>
        {stepChips.length === 0 ? (
          <span className="bodytext-11" style={{ color: "var(--soremo-light-gray)" }}>
            項目名を入力すると表示されます
          </span>
        ) : (
          stepChips.map(({ step, inWizard, order }) => (
            <StepChip
              key={step.key}
              $kind={step.isToggle ? "toggle" : step.isChoice ? "choice" : "fixed"}
            >
              {order !== null && `${order}. `}
              {step.title}
              {step.isToggle
                ? "（トグル）"
                : step.isChoice
                  ? `（${step.rows.length}択）`
                  : inWizard
                    ? "（数量のみ）"
                    : "（確認のみ）"}
            </StepChip>
          ))
        )}
      </div>

      <TableWrap>
        <StyledTable>
          <thead>
            <tr>
              <th style={{ width: 36 }} />
              <th className="bodytext-11" style={{ minWidth: 170 }}>
                項目名
              </th>
              <th className="bodytext-11" style={{ width: 140 }}>
                単価
              </th>
              <th className="bodytext-11" style={{ width: 72 }}>
                数量
              </th>
              <th className="bodytext-11" style={{ width: 72 }}>
                単位
              </th>
              <th className="bodytext-11" style={{ minWidth: 150 }}>
                備考
              </th>
              <th className="bodytext-11" style={{ width: 168 }}>
                数量可変
              </th>
              <th className="bodytext-11" style={{ width: 44 }}>
                詳細
              </th>
              <th className="bodytext-11" style={{ width: 44 }}>
                操作
              </th>
            </tr>
          </thead>
          <SortableList
            items={rows}
            getId={(row) => row.id}
            strategy="vertical"
            axis="y"
            onReorder={reorderRows}
          >
            <tbody>
              {rows.map((row) => {
                const { base, suffix } = parseRowName(row.name);
                const expanded = expandedRowIds.has(row.id);
                const hint = priceHint(row);
                const inChoiceGroup =
                  suffix !== null && choiceGroupBases.has(base);
                return (
                  <SortableItem key={row.id} id={row.id}>
                    {({ setNodeRef, style, handleProps }) => (
                      <>
                        <tr ref={setNodeRef} style={style}>
                          <td
                            style={{
                              verticalAlign: "middle",
                              ...(inChoiceGroup ? CHOICE_GROUP_ACCENT_STYLE : null),
                            }}
                          >
                            <SortableDragHandle {...handleProps} />
                          </td>
                          <td>
                            <CellInput
                              value={row.name}
                              placeholder="項目名（末尾 _a _b で選択肢）"
                              aria-label="項目名"
                              onChange={(e) =>
                                patchRow(row.id, { name: e.target.value })
                              }
                            />
                          </td>
                          <td>
                            <NumberCellInput
                              value={row.unitPrice}
                              placeholder="単価 / 項目名"
                              aria-label="単価"
                              onChange={(e) =>
                                patchRow(row.id, { unitPrice: e.target.value })
                              }
                            />
                            {hint && <CellHint>{hint}</CellHint>}
                          </td>
                          <td>
                            <NumberCellInput
                              value={String(row.quantity)}
                              inputMode="numeric"
                              aria-label="数量"
                              onChange={(e) =>
                                patchRow(row.id, {
                                  quantity: Math.max(
                                    0,
                                    parseIntOr(e.target.value, 0),
                                  ),
                                })
                              }
                            />
                          </td>
                          <td>
                            <CellInput
                              value={row.unit}
                              placeholder="単位（% で歩合）"
                              aria-label="単位"
                              onChange={(e) =>
                                patchRow(row.id, { unit: e.target.value })
                              }
                            />
                          </td>
                          <td>
                            <CellInput
                              value={row.note}
                              placeholder="備考"
                              aria-label="備考"
                              onChange={(e) =>
                                patchRow(row.id, { note: e.target.value })
                              }
                            />
                          </td>
                          <td>
                            <div className="u-row u-gap8 u-align-center">
                              <Toggle
                                value={row.quantityVariable}
                                onClick={() => {
                                  const next = !row.quantityVariable;
                                  patchRow(row.id, { quantityVariable: next });
                                  // ON にした直後は範囲を設定したいので詳細を開く
                                  if (next) expandRow(row.id);
                                }}
                              />
                              {row.quantityVariable && (
                                <RangeSummaryButton
                                  type="button"
                                  aria-label="数量可変の範囲を編集"
                                  onClick={() => toggleExpanded(row.id)}
                                >
                                  {row.quantityMin}〜{row.quantityMax} / 初期{" "}
                                  {row.quantityDefault}
                                </RangeSummaryButton>
                              )}
                            </div>
                          </td>
                          <td style={{ textAlign: "center" }}>
                            <IconButton
                              icon={expanded ? "expand_less" : "expand_more"}
                              size={20}
                              color={
                                hasDetail(row)
                                  ? "var(--soremo-blue)"
                                  : "var(--soremo-light-gray)"
                              }
                              onClick={() => toggleExpanded(row.id)}
                              aria-label="説明・サムネイルを編集"
                            />
                          </td>
                          <td>
                            {/* 複製・削除はアイコンを並べず 3 点リーダーに畳む */}
                            <div className="u-row u-justify-center">
                              <DropdownMenu
                                size="small"
                                items={[
                                  {
                                    label: "行を複製",
                                    icon: "content_copy",
                                    onClick: () => duplicateRow(row.id),
                                  },
                                  {
                                    label: "行を削除",
                                    icon: "delete",
                                    onClick: () => removeRow(row.id),
                                  },
                                ]}
                              />
                            </div>
                          </td>
                        </tr>
                        {expanded && (
                          <tr style={style}>
                            <td
                              style={
                                inChoiceGroup ? CHOICE_GROUP_ACCENT_STYLE : undefined
                              }
                            />
                            <DetailCell colSpan={8}>
                              <DetailGrid>
                                <div className="u-col u-gap4">
                                  <span
                                    className="bodytext-11"
                                    style={{ color: "var(--soremo-gray)" }}
                                  >
                                    項目説明テキスト（ステップ全体の説明として表示）
                                  </span>
                                  <DetailTextarea
                                    value={row.itemDescription}
                                    aria-label="項目説明テキスト"
                                    onChange={(e) =>
                                      patchRow(row.id, {
                                        itemDescription: e.target.value,
                                      })
                                    }
                                  />
                                </div>
                                <div className="u-col u-gap4">
                                  <span
                                    className="bodytext-11"
                                    style={{ color: "var(--soremo-gray)" }}
                                  >
                                    項目サムネイル（ステップ全体）
                                  </span>
                                  {renderThumbBox(row, "itemThumbnailUrl", "項目サムネイル")}
                                </div>
                                <div className="u-col u-gap4">
                                  <span
                                    className="bodytext-11"
                                    style={{ color: "var(--soremo-gray)" }}
                                  >
                                    オプションテキスト（この選択肢のカードに表示）
                                  </span>
                                  <DetailTextarea
                                    value={row.optionText}
                                    aria-label="オプションテキスト"
                                    onChange={(e) =>
                                      patchRow(row.id, {
                                        optionText: e.target.value,
                                      })
                                    }
                                  />
                                </div>
                                <div className="u-col u-gap4">
                                  <span
                                    className="bodytext-11"
                                    style={{ color: "var(--soremo-gray)" }}
                                  >
                                    オプションサムネイル（この選択肢）
                                  </span>
                                  {renderThumbBox(
                                    row,
                                    "optionThumbnailUrl",
                                    "オプションサムネイル",
                                  )}
                                </div>
                                {row.quantityVariable &&
                                  renderQuantityRangeEditor(row)}
                              </DetailGrid>
                            </DetailCell>
                          </tr>
                        )}
                      </>
                    )}
                  </SortableItem>
                );
              })}
            </tbody>
          </SortableList>
        </StyledTable>
      </TableWrap>

      <div className="u-row u-gap8">
        <Button
          size="small"
          theme="secondary"
          variant="outlined"
          width="fit"
          leftIcon="add"
          onClick={addRow}
        >
          行を追加
        </Button>
      </div>

      {aiTarget && aiRow && (
        <AiImageGeneratorModal
          scene="image_estimate_item"
          itemLabel={aiItemLabelOf(aiRow, aiIsOption)}
          itemDesc={aiItemDescOf(aiRow, aiIsOption) || undefined}
          planItems={steps.map((step) => step.title)}
          /* オプションサムネイルは選択肢カードで正方形表示されるので、
             生成も正方形にして横長画像のトリミング崩れを防ぐ */
          imageShape={aiIsOption ? "square" : "landscape"}
          onSelect={handleAiGenerated}
          onClose={() => setAiTarget(null)}
        />
      )}
    </div>
  );
};

export default ItemTableEditor;
